import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import * as api from '../services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Loads a chapter's verses (offline-first via api.getChapter) and tracks
 * loading / availability / total-chapters state. Keeps stale verses on error
 * so the reader never flashes empty. Returns setChapterVerses so callers can
 * patch verses (e.g. after inline edits).
 */
export const useChapterLoader = (selectedBook, selectedChapter, selectedTranslation) => {
  const [chapterVerses, setChapterVerses] = useState([]);
  const [chapterSource, setChapterSource] = useState(null); // 'offline' | 'network'
  const [isLoading, setIsLoading] = useState(false);
  const [isUnavailable, setIsUnavailable] = useState(false); // book not in this translation
  const [fallbackTranslation, setFallbackTranslation] = useState(null); // set when served a seeded fallback
  const [totalChapters, setTotalChapters] = useState(150);

  useEffect(() => {
    let isCancelled = false;

    const loadChapter = async () => {
      setIsLoading(true);
      setIsUnavailable(false);
      setFallbackTranslation(null);
      try {
        const response = await api.getChapter(selectedBook, selectedChapter, selectedTranslation);

        if (!isCancelled && response && response.verses && Array.isArray(response.verses) && response.verses.length > 0) {
          setChapterVerses(response.verses);
          setChapterSource(response.source === 'offline' ? 'offline' : 'network');
          setFallbackTranslation(response.is_fallback ? (response.fallback_translation || 'KJV') : null);
          setTotalChapters(response.total_chapters || 150);
          setIsLoading(false);
          // Smart-preload the next chapter so Next-page taps feel instant.
          api.preloadNextChapter(selectedBook, selectedChapter, selectedTranslation);
        } else if (!isCancelled && response && response.is_unavailable) {
          // Book not part of this translation (e.g. JPS is OT-only).
          setChapterVerses([]);
          setIsUnavailable(true);
          setIsLoading(false);
        } else if (!isCancelled && (!response || !response.verses || response.verses.length === 0)) {
          setChapterVerses(prevVerses => {
            if (prevVerses.length === 0) {
              toast.error('No verses found');
              return [];
            }
            return prevVerses;
          });
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error loading chapter:', error);
        if (!isCancelled) {
          setChapterVerses(prevVerses => {
            if (prevVerses.length === 0) {
              toast.error(`Failed to load ${selectedBook} ${selectedChapter}`);
              return [];
            }
            return prevVerses;
          });
          setIsLoading(false);
        }
      }
    };

    loadChapter();
    return () => { isCancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBook, selectedChapter, selectedTranslation]);

  return { chapterVerses, setChapterVerses, chapterSource, isLoading, isUnavailable, fallbackTranslation, totalChapters };
};

/**
 * Chapter-level verse-trail counts (how many study-trail jumps touch each
 * verse). Authenticated endpoint — skipped for guests to avoid 401 noise.
 */
export const useVerseTrail = (selectedBook, selectedChapter, isGuest) => {
  const [trailCounts, setTrailCounts] = useState({});

  useEffect(() => {
    if (isGuest) { setTrailCounts({}); return; }
    let cancelled = false;
    axios
      .get(`${BACKEND_URL}/api/lumera/verse-trail/chapter`, {
        params: { book: selectedBook, chapter: selectedChapter },
        withCredentials: true,
      })
      .then((r) => { if (!cancelled) setTrailCounts(r.data?.counts || {}); })
      .catch(() => { if (!cancelled) setTrailCounts({}); });
    return () => { cancelled = true; };
  }, [selectedBook, selectedChapter, isGuest]);

  return trailCounts;
};

/**
 * User's highlight colors keyed by verse reference. Authenticated — skipped
 * for guests. Returns setHighlights so save/remove handlers can patch the map.
 */
export const useChapterHighlights = (selectedBook, selectedChapter, selectedTranslation, isGuest) => {
  const [highlights, setHighlights] = useState({}); // verse_reference -> color

  useEffect(() => {
    if (isGuest) { setHighlights({}); return; }
    const loadHighlights = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/highlights/`, { withCredentials: true });
        if (response.data && Array.isArray(response.data)) {
          const highlightMap = {};
          response.data.forEach(h => { highlightMap[h.verse_reference || h.reference] = h.color; });
          setHighlights(highlightMap);
        }
      } catch (error) {
        // Silent — non-critical, and guests never reach here.
      }
    };
    loadHighlights();
  }, [selectedBook, selectedChapter, selectedTranslation, isGuest]);

  return [highlights, setHighlights];
};

/**
 * Single-verse highlight state + CRUD for the dashboard reader (BibleReader).
 * Loads the current verse's highlight whenever `currentVerse` changes; exposes
 * dialog state, color/note, and save/remove handlers. 404/401 clear silently.
 */
export const useVerseHighlight = (currentVerse) => {
  const [isHighlightDialogOpen, setIsHighlightDialogOpen] = useState(false);
  const [highlightColor, setHighlightColor] = useState('yellow');
  const [highlightNote, setHighlightNote] = useState('');
  const [currentHighlight, setCurrentHighlight] = useState(null);

  const loadHighlight = async () => {
    if (!currentVerse?.reference) return;
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/highlights/${encodeURIComponent(currentVerse.reference)}`,
        { withCredentials: true }
      );
      setCurrentHighlight(response.data);
      if (response.data) {
        setHighlightColor(response.data.color);
        setHighlightNote(response.data.note || '');
      }
    } catch (error) {
      if (error.response?.status === 404 || error.response?.status === 401) {
        setCurrentHighlight(null);
        setHighlightNote('');
      } else {
        console.error('Error loading highlight:', error);
      }
    }
  };

  useEffect(() => {
    loadHighlight();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentVerse]);

  const openHighlightDialog = () => {
    if (currentHighlight) {
      setHighlightColor(currentHighlight.color);
      setHighlightNote(currentHighlight.note || '');
    } else {
      setHighlightColor('yellow');
      setHighlightNote('');
    }
    setIsHighlightDialogOpen(true);
  };

  const saveHighlight = async () => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/highlights/`,
        {
          reference: currentVerse.reference,
          book: currentVerse.book,
          chapter: currentVerse.chapter,
          verse: currentVerse.verse,
          text: currentVerse.text,
          color: highlightColor,
          note: highlightNote,
        },
        { withCredentials: true }
      );
      toast.success('Verse highlighted!');
      await loadHighlight();
      setIsHighlightDialogOpen(false);
    } catch (error) {
      toast.error('Failed to save highlight');
    }
  };

  const removeHighlight = async () => {
    try {
      await axios.delete(
        `${BACKEND_URL}/api/highlights/${encodeURIComponent(currentVerse.reference)}`,
        { withCredentials: true }
      );
      toast.success('Highlight removed');
      setCurrentHighlight(null);
      setIsHighlightDialogOpen(false);
    } catch (error) {
      toast.error('Failed to remove highlight');
    }
  };

  return {
    isHighlightDialogOpen, setIsHighlightDialogOpen,
    highlightColor, setHighlightColor,
    highlightNote, setHighlightNote,
    currentHighlight, openHighlightDialog, saveHighlight, removeHighlight,
  };
};
